import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, storage, auth } from "../../Firebase_Config/firebaseConfig"; // ✅ import auth
import {
  ref,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import "./EditBlog.css";

const EditBlog = ({ setAlertMsg, setAlertColor }) => {
  const { blogId } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [blogImg, setBlogImg] = useState(null); // { file, url }
  const [imgPreview, setImgPreview] = useState("");
  const [oldImgPath, setOldImgPath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const hiddenFileInput = useRef(null);

  useEffect(() => {
    if (!blogId) {
      setAlertMsg("No blog selected!");
      setAlertColor("red");
      navigate("/MyBlogs");
      return;
    }

    const fetchBlog = async () => {
      try {
        const blogRef = doc(db, "blogPosts", blogId);
        const blogSnap = await getDoc(blogRef);

        if (!blogSnap.exists()) {
          setAlertMsg("Blog not found!");
          setAlertColor("red");
          navigate("/MyBlogs");
          return;
        }

        const data = blogSnap.data();
        setTitle(data.title || "");
        setContent(data.postText || "");
        if (data.ImgSrc) {
          setBlogImg({ file: null, url: data.ImgSrc });
          setImgPreview(data.ImgSrc);
        } else {
          setBlogImg(null);
          setImgPreview("");
        }
        setOldImgPath(data.ImgPath || null);
      } catch (err) {
        console.error("fetchBlog error:", err);
        setAlertMsg("Failed to fetch blog data.");
        setAlertColor("red");
        navigate("/MyBlogs");
      }
    };

    fetchBlog();
  }, [blogId, navigate, setAlertMsg, setAlertColor]);

  const handleFileChange = (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    const previewUrl = URL.createObjectURL(file);
    setBlogImg({ file, url: previewUrl });
    setImgPreview(previewUrl);
    console.log("Selected file:", file);
  };

  const removeFile = async () => {
    if (!blogId) return;
    setLoading(true);
    try {
      if (oldImgPath) {
        try {
          await deleteObject(ref(storage, oldImgPath));
          console.log("Deleted old image from storage:", oldImgPath);
        } catch (err) {
          console.warn("Failed to delete old image:", err);
        }
      }

      setBlogImg(null);
      setImgPreview("");
      setOldImgPath(null);
      if (hiddenFileInput.current) hiddenFileInput.current.value = null;

      const blogRef = doc(db, "blogPosts", blogId);
      await updateDoc(blogRef, { ImgSrc: null, ImgPath: null });

      setAlertMsg("Image removed");
      setAlertColor("lightblue");
    } catch (err) {
      console.error("removeFile error:", err);
      setAlertMsg("Failed to remove image");
      setAlertColor("red");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fixed handleUpdate
  const handleUpdate = async () => {
    if (!title.trim() || !content.trim()) {
      setAlertMsg("Title and content cannot be empty!");
      setAlertColor("red");
      return;
    }

    setLoading(true);
    try {
      let finalImgUrl = null;
      let finalImgPath = null;

      // CASE A: new file selected -> upload it
      if (blogImg?.file) {
        if (oldImgPath) {
          try {
            await deleteObject(ref(storage, oldImgPath));
            console.log("Deleted old storage image:", oldImgPath);
          } catch (err) {
            console.warn("Could not delete old storage image:", err);
          }
        }

        const newPath = `blogPosts/${auth.currentUser.uid}/${Date.now()}_${
          blogImg.file.name
        }`; // ✅ correct folder
        const storageRef = ref(storage, newPath);
        await uploadBytes(storageRef, blogImg.file);
        finalImgUrl = await getDownloadURL(storageRef);
        finalImgPath = newPath;

        if (blogImg.url && blogImg.url.startsWith("blob:")) {
          URL.revokeObjectURL(blogImg.url);
        }
      }

      // CASE B: keep existing image
      if (!blogImg?.file && blogImg?.url) {
        finalImgUrl = blogImg.url;
        finalImgPath = oldImgPath || null;
      }

      // CASE C: removed image => finalImgUrl stays null

      const blogRef = doc(db, "blogPosts", blogId);
      await updateDoc(blogRef, {
        title: title.trim(),
        postText: content.trim(),
        ImgSrc: finalImgUrl || null,
        ImgPath: finalImgPath || null,
      });

      setAlertMsg("Blog updated successfully!");
      setAlertColor("lightgreen");
      navigate("/MyBlogs");
    } catch (err) {
      console.error("handleUpdate error:", err);
      setAlertMsg("Failed to update blog.");
      setAlertColor("red");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="edit-blog-container">
      {loading && <div className="edit-blog-loader">Working...</div>}

      <h1 className="edit-blog-heading">Edit Blog</h1>
      <p className="edit-blog-subheading">
        Update your thoughts and ideas here.
      </p>

      <div className="edit-blog-form">
        <input
          type="text"
          className="edit-blog-input"
          placeholder="Blog Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <div
          className="edit-blog-upload-box"
          onClick={() =>
            hiddenFileInput.current && hiddenFileInput.current.click()
          }
        >
          {imgPreview ? (
            <div className="edit-blog-image-wrapper">
              <img
                src={imgPreview}
                alt="Blog Preview"
                className="edit-blog-preview-img"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowModal(true);
                }}
              />
              <button
                type="button"
                className="edit-blog-remove-img-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
              >
                <i className="fas fa-trash" style={{ color: "lightgray" }}></i>
              </button>
            </div>
          ) : (
            "Upload Image"
          )}
        </div>

        <input
          type="file"
          accept="image/*"
          ref={hiddenFileInput}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        <textarea
          className="edit-blog-textarea"
          placeholder="Start writing your blog here..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />

        <div className="edit-blog-buttons">
          <button
            className="edit-blog-submit-btn"
            onClick={handleUpdate}
            disabled={loading}
          >
            {loading ? "Working..." : "Update Blog"}
          </button>
          <button
            className="edit-blog-cancel-btn"
            type="button"
            onClick={() => navigate("/MyBlogs")}
          >
            Cancel
          </button>
        </div>
      </div>

      {showModal && (
        <div
          className="edit-blog-image-modal"
          onClick={() => setShowModal(false)}
        >
          <img
            src={imgPreview}
            alt="Full Preview"
            className="edit-blog-modal-img"
          />
        </div>
      )}
    </div>
  );
};

export default EditBlog;
